import { createContext, useContext, useEffect, useState } from "react";
import { business as staticBusiness } from "../data/siteData";

const ZeoContentContext = createContext(null);

const DEFAULT_THEME = { active: null, primary: "#A85C34", accent: "#D4A15C", banner_enabled: false, banner_text: "" };

function safeParse(json, fallback) {
  if (!json) return fallback;
  try {
    return JSON.parse(json);
  } catch {
    return fallback;
  }
}

// "0551 217 38 53" gibi ulusal formatı "+905512173853" biçimine çevirir —
// panelde tek bir telefon alanı yeterli olsun diye (tel:/wa.me linkleri için).
function toE164TR(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return "";
  const national = digits.startsWith("90") ? digits.slice(2) : digits.startsWith("0") ? digits.slice(1) : digits;
  return `+90${national}`;
}

// Panelden gelen sosyal medya linkleri doğrudan <a href> olarak render edilir
// (Footer, Store, Team, InstagramGallery). Yalnızca http(s) ile başlayan
// değerler kabul edilir — aksi halde (örn. "javascript:...") tıklanınca kod
// çalıştırabilecek bir href enjeksiyonu engellenmiş olur.
function safeUrl(value, fallback) {
  const trimmed = value?.trim();
  if (!trimmed) return fallback;
  return /^https?:\/\//i.test(trimmed) ? trimmed : fallback;
}

// "@handle" metnini Instagram URL'sinden türetir — panelden ("Site Yönetimi"
// → İletişim Bilgileri) girilen Instagram linki değiştiğinde ekrandaki
// "@..." başlığın da otomatik güncellenmesi için (önceden sabit bir metindi,
// panelde link değişince ekrandaki isim eskisiyle uyumsuz kalıyordu).
function deriveInstagramHandle(url, fallback) {
  try {
    const path = new URL(url).pathname.replace(/^\/+|\/+$/g, "");
    const handle = path.split("/")[0];
    return handle ? `@${handle}` : fallback;
  } catch {
    return fallback;
  }
}

function mergeBusiness(overrides) {
  if (!overrides) return staticBusiness;
  const phone = overrides.phone?.trim();
  const e164 = phone ? toE164TR(phone) : null;
  const instagram = safeUrl(overrides.instagram, staticBusiness.social.instagram);
  return {
    ...staticBusiness,
    phone: phone || staticBusiness.phone,
    phoneRaw: e164 || staticBusiness.phoneRaw,
    whatsapp: e164 || staticBusiness.whatsapp,
    address: overrides.address?.trim() || staticBusiness.address,
    hours: overrides.hours?.trim() || staticBusiness.hours,
    social: {
      ...staticBusiness.social,
      instagram,
      instagramHandle: deriveInstagramHandle(instagram, staticBusiness.social.instagramHandle),
      facebook: safeUrl(overrides.facebook, staticBusiness.social.facebook),
      whatsapp: e164 ? `https://wa.me/${e164.replace("+", "")}` : staticBusiness.social.whatsapp,
    },
  };
}

// ZEO Medya panelinin "Site & Sayfa Yönetimi" ekranından girilen içerik ve
// özel gün temasını /api/pages.php üzerinden (ZEO Connect proxy) çeker.
// ProductsProvider ile aynı desen: statik/varsayılan içerikle başlar, canlı
// veri geldiğinde üzerine yazar; API'ye ulaşılamazsa site hiçbir zaman
// bozuk görünmez — bileşenler kendi statik metinlerine geri döner.
export function ZeoContentProvider({ children }) {
  const [heroImage, setHeroImage] = useState(null);
  const [aboutText, setAboutText] = useState(null);
  const [faqs, setFaqs]           = useState(null);
  const [theme, setTheme]         = useState(DEFAULT_THEME);
  const [business, setBusiness]   = useState(staticBusiness);
  const [loaded, setLoaded]       = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await fetch("/api/pages.php");
        if (!res.ok) return;
        const json = await res.json();
        if (json.status === "error" || !json.pages) return;

        const pages = json.pages;
        if (cancelled) return;

        if (pages.hero_image?.content) setHeroImage(pages.hero_image.content);
        if (pages.about?.content) setAboutText(pages.about.content);

        const faqList = safeParse(pages.faq_json?.content, null);
        if (Array.isArray(faqList) && faqList.length > 0) setFaqs(faqList);

        const themeData = safeParse(pages.theme_json?.content, null);
        if (themeData) setTheme({ ...DEFAULT_THEME, ...themeData });

        const businessData = safeParse(pages.business_json?.content, null);
        if (businessData) setBusiness(mergeBusiness(businessData));
      } catch {
        // ZEO Connect'e ulaşılamadı — statik/varsayılan içerikle devam edilir.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  // Aktif temanın renklerini kök CSS değişkenlerine uygula — bu değişkenleri
  // zaten kullanan öğeler (bkz. index.css) anında yeni renge geçer.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--frida-primary", theme.primary);
    root.style.setProperty("--frida-accent", theme.accent);
  }, [theme.primary, theme.accent]);

  return (
    <ZeoContentContext.Provider value={{ heroImage, aboutText, faqs, theme, business, loaded }}>
      {children}
    </ZeoContentContext.Provider>
  );
}

export function useZeoContent() {
  const ctx = useContext(ZeoContentContext);
  if (!ctx) throw new Error("useZeoContent must be used within a ZeoContentProvider");
  return ctx;
}
